import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
  SetActualBase,
  SetActualFilters,
  SetEndDate,
  SetIgnoredDates,
  SetInitialDate,
  UpdateModifiedData,
  SetExcludePUDCItems,
  SetExcludeRCTDACItems,
  SetExcludeRCUDCItems,
  SetDetailRowData,
  SetDetailDate,
  SetResetStore,
  GetStandartDataDetails,
  SetStandarData,
  GetStandarDataWithBase,
  SetMedidorId,
} from './standar-prediction.action';
import { PredictionService } from '@/app/core/services/prediction/prediction.service';
import { MatDialog } from '@angular/material/dialog';
import { EnovaAlertsComponent } from '@/app/core/utils/enova-alerts/enova-alerts.component';

export interface StandartPredictionStateModel {
  modifiedData: any;
  actualBase: number;
  filters: { [key: string]: any };
  startDate: string;
  endDate: string;
  ignoredDates: any[];
  excludePUDCItems: any[];
  excludeRCTDACItems: any[];
  excludeRCUDCItems: any[];
  detailRowData: {};
  dateDetail: string | null;
  standarData: any[];
  graphStandartData: {
    labels: any[];
    datasets: any[];
  };
  medidorId: number | null;
}

const defaultState: StandartPredictionStateModel = {
  modifiedData: {
    'Último dato coincidente': 100,
    'Promedio de los últimos datos coincidentes': 0,
    'Razón de cambio de un día coincidente respecto a un periodo típico anterior': 0,
    'Razón de cambio respecto a los últimos datos coincidentes': 0,
  },
  actualBase: 3,
  filters: {},
  startDate: '',
  endDate: '',
  ignoredDates: [],
  excludePUDCItems: [],
  excludeRCTDACItems: [],
  excludeRCUDCItems: [],
  detailRowData: {},
  dateDetail: '',
  standarData: [],
  graphStandartData: {
    labels: [],
    datasets: [],
  },
  medidorId: null,
};

@State<StandartPredictionStateModel>({
  name: 'standarPrediction',
  defaults: defaultState,
})
@Injectable()
export class StandarPredictionState {
  @Selector()
  static getModifiedData(state: StandartPredictionStateModel) {
    return state.modifiedData;
  }

  @Selector()
  static getActualBase(state: StandartPredictionStateModel) {
    return state.actualBase;
  }
  @Selector()
  static getFilters(state: StandartPredictionStateModel) {
    return state.filters;
  }

  @Selector()
  static getStartDate(state: StandartPredictionStateModel) {
    return state.startDate;
  }

  @Selector()
  static getEndDate(state: StandartPredictionStateModel) {
    return state.endDate;
  }
  @Selector()
  static getIgnoredDate(state: StandartPredictionStateModel) {
    return state.ignoredDates;
  }

  @Selector()
  static getExcludePUDCItems(state: StandartPredictionStateModel) {
    return state.excludePUDCItems;
  }

  @Selector()
  static getExcludeRCTDACItems(state: StandartPredictionStateModel) {
    return state.excludeRCTDACItems;
  }

  @Selector()
  static getExcludeRCUDCItems(state: StandartPredictionStateModel) {
    return state.excludeRCUDCItems;
  }

  @Selector()
  static getDetailRowData(state: StandartPredictionStateModel) {
    return state.detailRowData;
  }

  @Selector()
  static getDetailDate(state: StandartPredictionStateModel) {
    return state.dateDetail;
  }

  @Selector()
  static getDataPredictionStandar(state: StandartPredictionStateModel) {
    return state.standarData;
  }
  @Selector()
  static getDataGraphStandar(state: StandartPredictionStateModel) {
    return state.graphStandartData;
  }

  @Selector()
  static getMedidorId(state: StandartPredictionStateModel) {
    return state.medidorId;
  }

  constructor(
    private predictionService: PredictionService,
    public dialog: MatDialog
  ) {}

  @Action(GetStandartDataDetails)
  async GetStandartDataDetails(
    ctx: StateContext<StandartPredictionStateModel>,
    action: GetStandartDataDetails
  ) {
    const { startDate, endDate, filters } = action.payload;

    try {
      const response = await this.predictionService.getDataStandartPrediction(
        startDate,
        endDate,
        filters
      );

      if (response) {
        const graphData =
          await this.predictionService.getGrapDataPrediction(response);

        if (graphData) {
          const datasets = [
            {
              label: 'UDC',
              data: graphData?.data['udc']?.map(row => row.valor),
              borderColor: '#ED7D31',
            },
            {
              label: 'PUDC',
              data: graphData?.data['pudc']?.map(row => row.valor),
              borderColor: '#A5A5A5',
              borderDash: [10, 5],
            },
            {
              label: 'RCDTAC',
              data: graphData?.data['rcdtac']?.map(row => row.valor),
              borderColor: '#FFC000',
              borderDash: [10, 5],
            },
            {
              label: 'RCUDC',
              data: graphData?.data['rcudc']?.map(row => row.valor),
              borderColor: '#5B9BD5',
            },
            {
              label: 'MPP',
              data: graphData?.data['mpp']?.map(row => row.valor),
              borderColor: '#000000',
            },
          ];
          ctx.patchState({
            graphStandartData: {
              labels: graphData.labels,
              datasets: datasets,
            },
          });
        }
        ctx.patchState({
          standarData: response,
        });
      } else {
        ctx.patchState({
          graphStandartData: {
            labels: [],
            datasets: [],
          },
          standarData: [],
        });
      }
    } catch (error) {
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          text: 'Ha ocurrido un error al obtener los datos',
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(GetStandarDataWithBase)
  async GetStandarDataWithBase(
    ctx: StateContext<StandartPredictionStateModel>,
    action: GetStandarDataWithBase
  ) {
    const { startDate, endDate, filters, base } = action.payload;
    try {
      const payload = {
        fechaInicio: startDate,
        fechaFin: endDate,
        baseCalculo: base,
        filters: filters,
      };
      const response =
        await this.predictionService.getDataStandartPredictionWithBase(payload);

      if (response) {
        const graphData =
          await this.predictionService.getGrapDataPrediction(response);

        if (graphData) {
          const datasets = [
            {
              label: 'UDC',
              data: graphData?.data['udc']?.map(row => row.valor),
              borderColor: '#ED7D31',
            },
            {
              label: 'PUDC',
              data: graphData?.data['pudc']?.map(row => row.valor),
              borderColor: '#A5A5A5',
              borderDash: [10, 5],
            },
            {
              label: 'RCDTAC',
              data: graphData?.data['rcdtac']?.map(row => row.valor),
              borderColor: '#FFC000',
              borderDash: [10, 5],
            },
            {
              label: 'RCUDC',
              data: graphData?.data['rcudc']?.map(row => row.valor),
              borderColor: '#5B9BD5',
            },
            {
              label: 'MPP',
              data: graphData?.data['mpp']?.map(row => row.valor),
              borderColor: '#000000',
            },
          ];
          ctx.patchState({
            graphStandartData: {
              labels: graphData.labels,
              datasets: datasets,
            },
          });
        }
        ctx.patchState({
          standarData: response,
        });
      } else {
        ctx.patchState({
          graphStandartData: {
            labels: [],
            datasets: [],
          },
          standarData: [],
        });
      }
    } catch (error) {
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          text: 'Ha ocurrido un error al obtener los datos',
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(UpdateModifiedData)
  updateModifiedData(
    ctx: StateContext<StandartPredictionStateModel>,
    action: UpdateModifiedData
  ) {
    const state = ctx.getState();
    const { key, value } = action.payload;

    const modifiedData = {
      ...state.modifiedData,
      [key]: value,
    };

    ctx.patchState({ modifiedData });
  }

  @Action(SetStandarData)
  async SetStandarData(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetStandarData
  ) {
    ctx.patchState({
      standarData: action.payload,
    });

    const graphData = await this.predictionService.getGrapDataPrediction(
      action.payload
    );

    if (graphData) {
      const datasets = [
        {
          label: 'UDC',
          data: graphData?.data['udc']?.map(row => row.valor),
          borderColor: '#ED7D31',
        },
        {
          label: 'PUDC',
          data: graphData?.data['pudc']?.map(row => row.valor),
          borderColor: '#A5A5A5',
          borderDash: [10, 5],
        },
        {
          label: 'RCDTAC',
          data: graphData?.data['rcdtac']?.map(row => row.valor),
          borderColor: '#FFC000',
          borderDash: [10, 5],
        },
        {
          label: 'RCUDC',
          data: graphData?.data['rcudc']?.map(row => row.valor),
          borderColor: '#5B9BD5',
        },
        {
          label: 'MPP',
          data: graphData?.data['mpp']?.map(row => row.valor),
          borderColor: '#000000',
        },
      ];
      ctx.patchState({
        graphStandartData: {
          labels: graphData.labels,
          datasets: datasets,
        },
      });
    }
  }

  @Action(SetActualBase)
  setActualBase(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetActualBase
  ) {
    ctx.patchState({
      actualBase: action.payload,
    });
  }

  @Action(SetActualFilters)
  SetActualFilters(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetActualFilters
  ) {
    ctx.patchState({
      filters: action.payload,
    });
  }

  @Action(SetInitialDate)
  SetInitialDate(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetInitialDate
  ) {
    ctx.patchState({
      startDate: action.payload,
    });
  }

  @Action(SetEndDate)
  SetEndDate(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetEndDate
  ) {
    ctx.patchState({
      endDate: action.payload,
    });
  }

  @Action(SetIgnoredDates)
  SetIgnoredDates(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetIgnoredDates
  ) {
    console.log(action.payload, 'action.payload');
    ctx.patchState({
      ignoredDates: action.payload,
    });
  }

  @Action(SetExcludePUDCItems)
  SetExcludePUDCItems(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetExcludePUDCItems
  ) {
    ctx.patchState({
      excludePUDCItems: action.payload,
    });
  }
  @Action(SetExcludeRCTDACItems)
  SetExcludeRCTDACItems(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetExcludeRCTDACItems
  ) {
    ctx.patchState({
      excludeRCTDACItems: action.payload,
    });
  }
  @Action(SetExcludeRCUDCItems)
  SetExcludeRCUDCItems(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetExcludeRCUDCItems
  ) {
    ctx.patchState({
      excludeRCUDCItems: action.payload,
    });
  }
  @Action(SetDetailRowData)
  SetDetailRowData(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetDetailRowData
  ) {
    ctx.patchState({
      detailRowData: action.payload,
    });
  }

  @Action(SetDetailDate)
  SetDetailDate(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetDetailDate
  ) {
    ctx.patchState({
      dateDetail: action.payload,
    });
  }

  @Action(SetMedidorId)
  SetMedidorId(
    ctx: StateContext<StandartPredictionStateModel>,
    action: SetMedidorId
  ) {
    ctx.patchState({
      medidorId: action.medidor,
    });
  }

  @Action(SetResetStore)
  SetResetStore(ctx: StateContext<StandartPredictionStateModel>) {
    ctx.patchState({
      modifiedData: {
        'Último dato coincidente': 100,
        'Promedio de los últimos datos coincidentes': 0,
        'Razón de cambio de un día coincidente respecto a un periodo típico anterior': 0,
        'Razón de cambio respecto a los últimos datos coincidentes': 0,
      },
      actualBase: 3,
      filters: {},
      ignoredDates: [],
      excludePUDCItems: [],
      excludeRCTDACItems: [],
      excludeRCUDCItems: [],
      detailRowData: {},
      dateDetail: '',
      graphStandartData: {
        labels: [],
        datasets: [],
      },
    });
  }
}
